import { useDispatch, useSelector } from 'react-redux';
import { SignupErrorType } from '../../types';
import { selectSignUpState, showAlertError, showDialogError } from '../../signUpSlice';
import { useSignupErrorFactory } from './useSignupErrorFactory';

/**
 * A hook for handling errors within the signup flow.
 *
 * Utilizes Redux for centralized storage and display of alert and dialog error types.
 *
 * @returns An object containing methods and state for managing signup flow errors.
 */
export const useSignupError = (openHelpDialog: () => void) => {
  const storeDispatch = useDispatch();
  const { isShowingAlertError, alertError, isShowingDialogError, dialogError } =
    useSelector(selectSignUpState);
  const {
    isSignupAlertError,
    createSignupAlertError,
    isSignupDialogError,
    createSignupDialogError,
  } = useSignupErrorFactory(openHelpDialog);

  const showError = (error: SignupErrorType) => {
    if (isSignupAlertError(error)) {
      storeDispatch(showAlertError(createSignupAlertError(error)));
      return;
    }

    if (isSignupDialogError(error)) {
      storeDispatch(showDialogError(createSignupDialogError(error)));
      return;
    }

    console.error('Unable to show signup error. Unknown error type:', error);
  };

  return {
    /** A boolean indicating if an alert error is being shown. */
    isShowingAlertError,
    /** The current alert error being shown. */
    alertError,
    /** A boolean indicating if a dialog error is being shown. */
    isShowingDialogError,
    /** The current dialog error being shown. */
    dialogError,
    /** Shows the provided error within the signup flow.
     *
     * @param error - The type of error to show.
     */
    showError,
  };
};
